/**
 * `/agents` legacy-link redirect stubs (RO-3-02) — straight ports of six
 * `excellent/app/agents/**` pages whose entire body is a `next/navigation`
 * `redirect()`. Same recipe the root route already uses for the shell's own
 * redirect cascade (`shell/redirects.ts`): a plain string destination, thrown
 * from `beforeLoad` as a typed `redirect({ to, replace })`.
 *
 * LIVE-CLOSEOUT: leftover fleet/analytics/runs URLs hop to `/`. They must not land on `/agents/analytics`,
 * which remounts ProductChrome. Child `beforeLoad` still fires on in-tree
 * navigations after root has already loaded.
 *
 * `/o/$uid` is still a future port; `agentsObjectRoute` keeps the correct
 * long-term path (the object page is a different item).
 */

import { z } from "zod";
import { createRoute, redirect } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { routeSearchSchema } from "../shell/search";

const rangeSearchSchema = routeSearchSchema({
  /** Legacy `?range=` on fleet/analytics URLs. Dropped at the hop; kept here so
   *  the param still validates. */
  range: z.string().optional(),
});

/** Hop leftover `/agents/*` URLs onto the dashboard. Never `/agents/analytics`. */
function dashboardHop(searchStr: string = "") {
  void searchStr;
  throw redirect({
    href: "/",
    replace: true,
  });
}

function analyticsRedirect() {
  return ({ location }: { location: { searchStr: string } }) => {
    dashboardHop(location.searchStr);
  };
}

/** Port of `agents/fleet/page.tsx` + `agents/analytics/fleet/page.tsx`. */
export const agentsFleetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/fleet",
  validateSearch: rangeSearchSchema,
  beforeLoad: analyticsRedirect(),
});

export const agentsFleetQualityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/fleet/quality",
  validateSearch: rangeSearchSchema,
  beforeLoad: analyticsRedirect(),
});

export const agentsAnalyticsFleetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/analytics/fleet",
  validateSearch: rangeSearchSchema,
  beforeLoad: analyticsRedirect(),
});

export const agentsAnalyticsFleetQualityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/analytics/fleet/quality",
  validateSearch: rangeSearchSchema,
  beforeLoad: analyticsRedirect(),
});

/**
 * Port of `agents/runs/page.tsx` (#43500) — the standalone Runs page is
 * retired. Hop to the dashboard; do not remount ProductChrome via Analytics.
 */
export const agentsRunsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/runs",
  beforeLoad: analyticsRedirect(),
});

/**
 * Port of `agents/o/[uid]/page.tsx` (Issue 25 pt2) — the legacy per-mode
 * object URL, consolidated onto the ONE canonical `/o/<uid>` surface. Carries
 * its origin mode through as `?from=agents` so the rail lights, the
 * breadcrumb reads Agents, and the context is preserved.
 */
export const agentsObjectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/o/$uid",
  beforeLoad: ({ params }) => {
    throw redirect({ to: `/o/${params.uid}?from=agents`, replace: true });
  },
});
